import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "../configs/routes";
import { GA4EventName } from "../types/analytics";
import { API_BASE_URL, API_ENDPOINTS, API_SA_BASE_URL } from "../constants/api";
import { CLIENT_ID, DL_CLIENT_ID } from "../constants/appConstants";
import { GEOIP_CHINA_COUNTRY_CODE } from "../constants/locale";
import config from "../constants/serverConfig";
import {
  AUTH_TYPE,
  EMAIL_LOGIN_VERIFICATION_RESULT,
  SHOW_TIMEOUT_DIALOG,
  USER_DATA_NAMESPACE,
  USER_SESSION_EXPIRED_TIME,
} from "../constants/storage";
import { URLS } from "../constants/urls";
import { LocalStorage } from "../services/storage";
import { ApplicationState } from "../store";
import { resetLanguage } from "../store/actions/layout-actions";
import { actionLogout } from "../store/actions/user-actions";
import {
  actionResetAuth,
  actionSetAuthLoggedIn,
  actionSetAuthLoggingOut,
  actionSetAuthStatus,
  actionSetAuthType,
} from "../store/actions/auth-actions";
import { clearAuthStorage } from "../services/auth-storage";
import { LayoutState } from "../store/reducers/layout-reducer";
import { UserState } from "../store/reducers/user-reducer";
import {
  AuthStatus,
  AuthType,
  ConfirmPassEntity,
  DetectAuthResult,
  LoginParams,
  SignInEntity,
} from "../types/auth";
import { ServiceEntity } from "../types/services";
import { REQUEST_TYPE } from "../types/user";
import GoogleAnalyticsV2 from "../utils/GoogleAnalyticsV2";
import { UtilsRequest } from "../utils/UtilsRequest";
import { generateToken } from "../utils/UtilsTokenGenerator";

declare global {
  interface Window {
    samsung: {
      account: {
        signIn: (locale: SignInEntity, configs: { locale: string }) => void;
      };
    };
  }
}

export const useAuthProvider = () => {
  const dispatch = useDispatch();
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const authState = useSelector<
    ApplicationState,
    { type: AuthType; isLoggedIn: boolean; isLoggingOut: boolean }
  >((state) => state.auth);
  const isLoggingOut = authState.isLoggingOut;

  /**
   * Detect auth type from USER_DATA_NAMESPACE (actual persisted user data).
   *
   * Non-destructive: it can promote status to "authenticated", but never
   * demotes to "anonymous". Absent USERDATA means "cannot tell from here" —
   * the session lives in an HttpOnly cookie this code cannot read — so only a
   * confirmed profile failure, a 401, or an explicit logout may mark the user
   * anonymous. Callers decide what to do with a false result.
   */
  const detectAuthType = (): DetectAuthResult => {
    // Check if USERDATA exists in localStorage (the actual persisted user data)
    const userDataJson = LocalStorage.get(USER_DATA_NAMESPACE);

    if (userDataJson) {
      try {
        const userData = JSON.parse(userDataJson);

        // USERDATA exists and is populated → user is logged in
        if (userData?.name) {
          // Determine auth type from stored AUTH_TYPE or email verification flag
          const storedAuthType = LocalStorage.get(AUTH_TYPE) as AuthType;

          if (storedAuthType === "EMAIL") {
            const emailLoginVerificationResult = LocalStorage.get(
              EMAIL_LOGIN_VERIFICATION_RESULT
            );
            const isEmailOTPverified =
              emailLoginVerificationResult &&
              emailLoginVerificationResult === "true";

            if (isEmailOTPverified) {
              dispatch(actionSetAuthType("EMAIL"));
              dispatch(actionSetAuthStatus("authenticated"));
              return { type: "EMAIL" as AuthType, isLoggedIn: true };
            }
          } else if (storedAuthType === "SA" || storedAuthType === "DL") {
            dispatch(actionSetAuthType(storedAuthType));
            dispatch(actionSetAuthStatus("authenticated"));
            return { type: storedAuthType, isLoggedIn: true };
          } else {
            // No stored auth type but USERDATA exists; determine from user data if possible
            const type =
              userData.userType === "DL"
                ? ("DL" as AuthType)
                : ("SA" as AuthType);
            dispatch(actionSetAuthType(type));
            dispatch(actionSetAuthStatus("authenticated"));
            return { type, isLoggedIn: true };
          }
        }
      } catch (e) {
        console.error("Failed to parse USERDATA:", e);
      }
    }

    // Could not confirm a session from local state. Deliberately no dispatch.
    return { type: null as AuthType, isLoggedIn: false };
  };

  /**
   * Returns current auth.isLoggedIn from Redux selector.
   */
  const validateSession = (): boolean => {
    return authState.isLoggedIn;
  };

  /**
   * Set auth type in Redux. Other hooks should use this instead of
   * dispatching actionSetAuthType directly.
   */
  const setAuthType = useCallback(
    (type: AuthType) => {
      dispatch(actionSetAuthType(type));
    },
    [dispatch]
  );

  /**
   * Set auth isLoggedIn in Redux. Other hooks should use this instead of
   * dispatching actionSetAuthLoggedIn directly.
   */
  const setAuthLoggedIn = useCallback(
    (value: boolean) => {
      dispatch(actionSetAuthLoggedIn(value));
    },
    [dispatch]
  );

  /**
   * Set the auth resolution status in Redux. Prefer this over setAuthLoggedIn:
   * it can express "still resolving", which route guards need.
   */
  const setAuthStatus = useCallback(
    (status: AuthStatus) => {
      dispatch(actionSetAuthStatus(status));
    },
    [dispatch]
  );

  /**
   * Unified login method with type-aware logic.
   * For "SA": uses CLIENT_ID, calls window.samsung.account.signIn.
   * For "DL": uses DL_CLIENT_ID, calls window.samsung.account.signIn.
   * For "EMAIL": no-op (email login handled by OTP flow).
   */
  const login = (type: AuthType, params?: LoginParams) => {
    if (type === "EMAIL") {
      // Email login is handled by the OTP flow, no-op here
      return;
    }

    const redirectURL = params?.redirectURL ?? "";
    const isDigitalLegacy = type === "DL" || params?.isDigitalLegacy === true;

    // cleanup the persisted data in redux to ensure that the profile, and GEOIP API will be called after successfull login
    // note: this will just reset the redux state to it's default value
    dispatch(actionLogout());
    dispatch(actionResetAuth());

    const appState = generateToken(32);
    const APP_ID = isDigitalLegacy ? DL_CLIENT_ID : CLIENT_ID;
    const authUrl = `${API_BASE_URL}${API_ENDPOINTS.AUTH_SIGNIN_URL}?returnUrl=${API_BASE_URL}${redirectURL}?success=1&appId=${APP_ID}`;

    LocalStorage.remove(USER_SESSION_EXPIRED_TIME);
    GoogleAnalyticsV2.trackEvent(GA4EventName.LoginAttempt, {
      login_method: "sa",
    });

    const paramsEntity: SignInEntity = {
      responseType: "code",
      clientId: APP_ID,
      redirectUri: encodeURIComponent(authUrl),
      state: appState,
      scope: "",
    };

    window.samsung.account.signIn(
      { ...paramsEntity },
      { locale: `${layoutState.languageCode}-${layoutState.countryCode}` }
    );
  };

  /**
   * Logout handler.
   * Navigates the browser to the backend logout URL to expire HttpOnly tokens.
   * Clears frontend auth state (USERDATA, AUTH_TYPE, EMAIL_LOGIN_VERIFICATION_RESULT).
   * DL logout redirects to China localsite if userCountryCode === 'CHN'.
   * Dispatches actionResetAuth().
   * The isLoggingOut flag stays true so the LogoutLoading overlay is visible
   * until the browser navigates to the backend logout URL.
   */
  const logout = useCallback(
    async (isIdle: boolean = false) => {
      dispatch(actionSetAuthLoggingOut(true));
      const currentAuthType = authState.type;

      // Determine where to redirect after logout completes
      let returnUrl = `${config.APP_BASE_URL_NO_PARAMS}/`;
      if (
        currentAuthType === "DL" &&
        userCountryCode === GEOIP_CHINA_COUNTRY_CODE
      ) {
        returnUrl = URLS.CHINA_LOCALSITE;
      }

      // For idle timeout, set the flag so the timeout dialog shows when the
      // page reloads after the backend redirect.
      if (isIdle) {
        localStorage.setItem(SHOW_TIMEOUT_DIALOG, "true");
      }

      // Clear frontend auth state before navigating to the backend logout URL.
      dispatch(resetLanguage());
      dispatch(actionLogout());
      dispatch(actionResetAuth());

      clearAuthStorage();

      // GA tracking for logout action
      GoogleAnalyticsV2.trackEvent(GA4EventName.Logout);

      // Navigate to the backend logout URL (not an API call).
      // The backend will expire HttpOnly auth tokens and redirect back to returnUrl.
      const logoutUrl = `${API_BASE_URL}${API_ENDPOINTS.AUTH_SIGNOUT_URL}?returnUrl=${encodeURIComponent(returnUrl)}`;
      window.location.replace(logoutUrl);

      // isLoggingOut stays true — the loading overlay shows until the browser
      // navigates away to the backend logout URL.
    },
    [authState.type, userCountryCode, dispatch]
  );

  const confirmPassword = (
    services: Array<ServiceEntity>,
    requestType: REQUEST_TYPE
  ) => {
    const arrServices: string[] = [];

    services.forEach((service) => {
      arrServices.push(service.service_code);
    });

    UtilsRequest.setRequestInfo(arrServices, requestType);
    const appState = generateToken(32);

    const redirectConf = `${API_BASE_URL}${ROUTES.MYDATA_HISTORY}`;
    const goBackUrl = `${config.APP_BASE_URL_NO_PARAMS}${ROUTES.MYDATA}`;
    const paramsEntity: ConfirmPassEntity = {
      locale: layoutState.languageCode,
      countryCode: layoutState.countryCode,
      redirect_uri: redirectConf,
      state: appState,
      goBackURL: goBackUrl,
    };

    const params = new URLSearchParams({ ...paramsEntity });

    window.location.replace(
      `${API_SA_BASE_URL}${API_ENDPOINTS.CONFIRM_PASS_URL}?${params}`
    );
  };

  return {
    login,
    logout,
    isLoggingOut,
    confirmPassword,
    detectAuthType,
    validateSession,
    setAuthType,
    setAuthLoggedIn,
    setAuthStatus,
  };
};
